import { useRef, useEffect, useState, useCallback } from "react";
import FlowNode from "./FlowNode";

// GAME AI DECISION SYSTEM
// Features: 3-way split from Evaluate node, 2 loopback arcs
//
// Layout:
//                    [Attack] ─────┐
//                       ↗          ↓
// [Sense] → [Evaluate] → [Defend] → [Merge] → [Resolve] → [Output]
//                       ↘          ↑
//                    [Flee] ───────┘
//                       │
//                       └──── loopback to Evaluate
//                                   │
//              Resolve ─────────────┴──── loopback to Sense

const NODE_CONFIG = {
  sense: { col: 0, row: 0, label: "Sense", sublabel: "Input", shape: "circle" },
  evaluate: {
    col: 1,
    row: 0,
    label: "Evaluate",
    sublabel: "Decide",
    shape: "diamond",
  },
  attack: {
    col: 2,
    row: -1,
    label: "Attack",
    sublabel: "Aggressive",
    shape: "hexagon",
  },
  defend: {
    col: 2,
    row: 0,
    label: "Defend",
    sublabel: "Cautious",
    shape: "hexagon",
  },
  flee: {
    col: 2,
    row: 1,
    label: "Flee",
    sublabel: "Retreat",
    shape: "hexagon",
  },
  merge: {
    col: 3,
    row: 0,
    label: "Execute",
    sublabel: "Action",
    shape: "diamond",
  },
  resolve: {
    col: 4,
    row: 0,
    label: "Resolve",
    sublabel: "Result",
    shape: "hexagon",
  },
  output: {
    col: 5,
    row: 0,
    label: "Output",
    sublabel: "Complete",
    shape: "ring",
  },
};

// Connections with two backward (loopback) connections
const CONNECTIONS = [
  // Forward flow
  { from: "sense", to: "evaluate", type: "wait" },
  // 3-way split from evaluate
  { from: "evaluate", to: "attack", type: "wait" }, // Diagonal up
  { from: "evaluate", to: "defend", type: "wait" }, // Horizontal
  { from: "evaluate", to: "flee", type: "wait" }, // Diagonal down
  // Merge paths
  { from: "attack", to: "merge", type: "wait" }, // Diagonal down
  { from: "defend", to: "merge", type: "wait" }, // Horizontal
  { from: "flee", to: "merge", type: "wait" }, // Diagonal up
  // Continue to output
  { from: "merge", to: "resolve", type: "wait" },
  { from: "resolve", to: "output", type: "wait" },
  // LOOPBACK 1: Flee reconsiders (smaller arc)
  { from: "flee", to: "evaluate", type: "backward" },
  // LOOPBACK 2: Resolve restarts cycle (larger arc)
  { from: "resolve", to: "sense", type: "backward" },
];

// Grid settings
const COL_WIDTH = 100;
const ROW_HEIGHT = 70;
const LOOPBACK_ARC_PADDING = 90;

const GameAIVisualization = ({
  currentNode = null,
  activePath = "defend", // 'attack', 'defend', or 'flee'
  isProcessing = false,
  loopCount = 0,
  onNodeClick,
}) => {
  const containerRef = useRef(null);
  const [nodePositions, setNodePositions] = useState({});

  const calculatePositions = useCallback(() => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const positions = {};

    const cols = Object.values(NODE_CONFIG).map((n) => n.col);
    const rows = Object.values(NODE_CONFIG).map((n) => n.row);
    const minCol = Math.min(...cols);
    const maxCol = Math.max(...cols);
    const minRow = Math.min(...rows);
    const maxRow = Math.max(...rows);

    const gridWidth = (maxCol - minCol) * COL_WIDTH;
    const gridHeight = (maxRow - minRow) * ROW_HEIGHT;

    const offsetX = (containerRect.width - gridWidth) / 2 - minCol * COL_WIDTH;
    const offsetY =
      (containerRect.height - gridHeight) / 2 - minRow * ROW_HEIGHT;

    Object.entries(NODE_CONFIG).forEach(([nodeId, config]) => {
      positions[nodeId] = {
        x: offsetX + config.col * COL_WIDTH,
        y: offsetY + config.row * ROW_HEIGHT,
      };
    });

    setNodePositions(positions);
  }, []);

  useEffect(() => {
    calculatePositions();
    window.addEventListener("resize", calculatePositions);
    return () => window.removeEventListener("resize", calculatePositions);
  }, [calculatePositions]);

  // Determine node active state
  const getNodeState = (nodeId) => {
    const isCurrentNode = currentNode === nodeId;

    // Define which nodes are on each path
    const pathNodes = {
      attack: ["sense", "evaluate", "attack", "merge", "resolve", "output"],
      defend: ["sense", "evaluate", "defend", "merge", "resolve", "output"],
      flee: ["sense", "evaluate", "flee", "merge", "resolve", "output"],
    };

    const activeNodes = pathNodes[activePath] || pathNodes.defend;
    const isOnActivePath = isProcessing && activeNodes.includes(nodeId);

    return {
      isActive: isCurrentNode || isOnActivePath,
      intensity: isCurrentNode ? 4 : isOnActivePath ? 2 : 0,
    };
  };

  // Determine connection active state
  const getConnectionState = (from, to, type) => {
    // Define active connections for each path
    const pathConnections = {
      attack: [
        "sense-evaluate",
        "evaluate-attack",
        "attack-merge",
        "merge-resolve",
        "resolve-output",
      ],
      defend: [
        "sense-evaluate",
        "evaluate-defend",
        "defend-merge",
        "merge-resolve",
        "resolve-output",
      ],
      flee: [
        "sense-evaluate",
        "evaluate-flee",
        "flee-merge",
        "merge-resolve",
        "resolve-output",
        "flee-evaluate",
      ],
    };

    const connectionId = `${from}-${to}`;
    const activeConnections =
      pathConnections[activePath] || pathConnections.defend;

    // Loopback from resolve is always potentially active
    const isResolveLoopback = connectionId === "resolve-sense";
    const isFleeLoopback = connectionId === "flee-evaluate";

    let isActive = false;
    if (isProcessing) {
      if (type === "backward") {
        // Loopbacks are active based on loop count or flee path
        if (isResolveLoopback && loopCount > 0) isActive = true;
        if (isFleeLoopback && activePath === "flee") isActive = true;
      } else {
        isActive = activeConnections.includes(connectionId);
      }
    }

    return {
      stroke: isActive ? "rgba(74,158,181,0.6)" : "rgba(74,158,181,0.25)",
      active: isActive,
    };
  };

  const hasPositions = Object.keys(nodePositions).length > 0;

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        position: "relative",
        minHeight: "340px",
        padding: "50px 20px",
      }}
    >
      {/* SVG Layer for Connections */}
      {hasPositions && (
        <svg
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          {CONNECTIONS.map(({ from, to, type }) => {
            const fromPos = nodePositions[from];
            const toPos = nodePositions[to];
            if (!fromPos || !toPos) return null;

            const { stroke, active } = getConnectionState(from, to, type);

            // Path calculation
            const isLoopback = toPos.x < fromPos.x;
            const isDiagonal = fromPos.y !== toPos.y && !isLoopback;
            const curveAmount = COL_WIDTH;

            let pathD;
            if (isLoopback) {
              // LOOPBACK ARC: Semi-circular arc below node system
              const allYPositions = Object.values(nodePositions).map(
                (p) => p.y,
              );
              const lowestY = Math.max(...allYPositions);

              const horizontalDist = fromPos.x - toPos.x;
              const arcRadius = horizontalDist / 2;
              const arcBottomY = lowestY + LOOPBACK_ARC_PADDING;

              // SVG Arc path: down → arc → up
              pathD = `M ${fromPos.x},${fromPos.y}
                                     L ${fromPos.x},${arcBottomY - arcRadius}
                                     A ${arcRadius} ${arcRadius} 0 0 1 ${toPos.x},${arcBottomY - arcRadius}
                                     L ${toPos.x},${toPos.y}`;
            } else if (isDiagonal) {
              // DIAGONAL: Bezier curve
              const curveStartX = toPos.x - curveAmount;

              pathD = `M ${fromPos.x},${fromPos.y}
                                     L ${curveStartX},${fromPos.y}
                                     C ${curveStartX + curveAmount * 0.5},${fromPos.y}
                                       ${toPos.x - curveAmount * 0.3},${toPos.y}
                                       ${toPos.x},${toPos.y}`;
            } else {
              // HORIZONTAL: Straight line
              pathD = `M ${fromPos.x},${fromPos.y} L ${toPos.x},${toPos.y}`;
            }

            // Styling
            const connectionStroke =
              type === "backward"
                ? active
                  ? "#9e6b4a"
                  : "rgba(158,107,74,0.3)"
                : stroke;
            const connectionDash = type === "backward" ? "4 4" : "none";
            const pulseColor = type === "backward" ? "#9e6b4a" : "#4a9eb5";

            return (
              <g key={`${from}-${to}`}>
                <path
                  d={pathD}
                  fill="none"
                  stroke={connectionStroke}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray={
                    connectionDash === "none" ? undefined : connectionDash
                  }
                />
                {/* Animated pulse */}
                {active && (
                  <circle r="3" fill={pulseColor}>
                    <animateMotion
                      dur={type === "backward" ? "2.5s" : "1.5s"}
                      repeatCount="indefinite"
                      path={pathD}
                    />
                  </circle>
                )}
              </g>
            );
          })}
        </svg>
      )}

      {/* Node Layer */}
      {hasPositions &&
        Object.entries(NODE_CONFIG).map(([nodeId, config]) => {
          const pos = nodePositions[nodeId];
          if (!pos) return null;

          const state = getNodeState(nodeId);

          return (
            <div
              key={nodeId}
              style={{
                position: "absolute",
                left: pos.x,
                top: pos.y,
                transform: "translate(-50%, -50%)",
              }}
            >
              <FlowNode
                id={nodeId}
                label={config.label}
                sublabel={config.sublabel}
                shape={config.shape}
                isActive={state.isActive}
                intensity={state.intensity}
                onClick={() => onNodeClick?.(nodeId)}
              />
            </div>
          );
        })}

      {/* Status Row */}
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          left: 0,
          right: 0,
          textAlign: "center",
        }}
      >
        <p
          style={{
            fontSize: "10px",
            color: isProcessing ? "#4a9eb5" : "#6a7080",
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            margin: 0,
          }}
        >
          {isProcessing
            ? `AI Decision: ${activePath.toUpperCase()}${loopCount > 0 ? ` (Loop ${loopCount})` : ""} - ${currentNode || "Processing"}`
            : "Click Sense to start AI cycle"}
        </p>
      </div>
    </div>
  );
};

export default GameAIVisualization;
